import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Card, Typography, Autocomplete, TextField, Container, Tooltip, Stepper, Step, StepLabel } from "@mui/material";
import axios from "axios";
import jwtDecode from "jwt-decode"; // npm install jwt-decode
import QuoteSelection from "./QuoteSelection";
import ProposalSection from "./ProposalSection";
import { baseUrl } from "../../Services/Constants";
import { processAndUpdateToken } from "../../Services/CommonFunctions";
import { useLocation, useNavigate } from "react-router-dom";

const ProposalBuilderCommonComponent = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedQuotes, setSelectedQuotes] = useState({});
  const [sectionStatus, setSectionStatus] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isFetchingSections, setIsFetchingSections] = useState(false);
  const [editSelection, setEditSelection] = useState(null);
  const location = useLocation();
  const editData = location.state;

  let url = new URL(window.location.href);
  let urlParamToken = url?.searchParams.get("token");

  useEffect(() => {
    const loadClients = async () => {
      let url = new URL(window.location.href);
      let urlParamToken = url?.searchParams.get("token");
      const decode_UserName = jwtDecode(urlParamToken || "");
      const csrQuoteAuthUserName = decode_UserName?.preferred_username;

      let token = sessionStorage.getItem("token");
      token = await processAndUpdateToken(token);

      const name = csrQuoteAuthUserName ? csrQuoteAuthUserName?.split("@")[0] : "User";
      const clientToGetBroker = {
        Email: csrQuoteAuthUserName
      }
      try {
        const res = await axios.post(
          `${baseUrl}/api/ProposalBuilder/GetClient/GetClientsForBroker`, clientToGetBroker,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const cleanedData = res?.data?.data?.map(s => s.replace(/,$/, '').trim());
        if (res.data?.success) setClientOptions(res?.data?.data);
        sessionStorage.setItem("proposalBrokerId", res?.data?.brokerId);
      } catch (e) {
        console.error("Error fetching clients:", e);
      }
    };

    const initialize = async () => {
      await loadClients();
    };

    initialize();
  }, []);

  useEffect(() => {
    setSelectedQuotes({});
  }, [selectedClient]);

  useEffect(() => {
    if (editData?.editMode) {
      setSelectedClient(editData.clientName);
      setEditSelection(editData);
      setCurrentStep(1);
    }
  }, [editData]);

  const selectedProposalData = useMemo(() => {
    return Object.values(selectedQuotes);
  }, [selectedQuotes]);

  const fetchProposalSectionStatus = async () => {
    const token = await processAndUpdateToken(sessionStorage.getItem("token"));

    const payload = {
      Data: Object.values(selectedQuotes).map(item => ({
        JobCode: item.JobCode,
        ClientName: selectedClient,
        lobName: item.lobName,
        files: item.files
      }))
    };

    return axios.post(
      `${baseUrl}/api/ProposalBuilder/GetProposalSectionStatus/GetProposalData`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
  };

  const steps = ["Select Client", "Quote Selection", "Proposal Sections"];

  return (
    <Box sx={{ bgcolor: "#f9fafb", minHeight: "100vh", width: "100vw", }}>
      <Container maxWidth="lg" sx={{ pt: "55px" }}>

        {/* 3-Step Stepper */}
        <Box sx={{ mb: 3, pt: 2 }}>
          <Stepper
            activeStep={currentStep}
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: "13px",
                fontWeight: 600,
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "#111827",
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "rgb(22 163 74)",
              },
              "& .MuiStepLabel-label.Mui-disabled": {
                color: "#9ca3af",
              },
              "& .MuiStepIcon-root": {
                color: "#d1d5db",
                fontSize: "28px",
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: "#2563eb",
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: "rgb(22 163 74)",
              },
              "& .MuiStepConnector-line": {
                borderColor: "#e5e7eb",
                borderTopWidth: "2px",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {currentStep > 0 && (
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1 }}>
            Client: {selectedClient}
          </Typography>
        )}

        {currentStep === 0 && (
          <Box
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              padding: "48px 56px",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#111827",
                marginBottom: "20px"
              }}
            >
              Client Name
            </Typography>

            <Autocomplete
              options={clientOptions}
              value={selectedClient}
              onChange={(_, val) => setSelectedClient(val)}
              getOptionLabel={(option) => option}
              sx={{
                width: "40%",
                mb: 5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563eb",
                  },
                },
                "& .MuiAutocomplete-input": {
                  padding: "3px 3px !important",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Client Name"
                />
              )}
            />


            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={() =>
                navigate(`/createproposalbuilder?token=${urlParamToken}`)}
                sx={{
                  textTransform: "none",
                  pl: "22px",
                  pr: "24px",
                  pt: 0,
                  pb: 0,
                  borderRadius: "5px",
                  height: "35px",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  border: "1px solid #e5e7eb",
                  // backgroundColor: "#f9fafb",
                  color: "#1f2937",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "white",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#000000f5",
                    color: "#000000f5",
                  },
                }}>Back</Button>

              <Button
                variant="contained"
                disabled={!selectedClient}
                onClick={() => setCurrentStep(1)}
                sx={{
                  textTransform: "none",
                  pl: "22px",
                  pr: "24px",
                  pt: 0,
                  pb: 0,
                  borderRadius: "5px",
                  height: "35px",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  backgroundColor: "#2563eb",
                  color: "white",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 1 && (
          <QuoteSelection
            brokerId={sessionStorage.getItem("proposalBrokerId")}
            clientName={selectedClient}
            selectedQuotes={selectedQuotes}
            onSelectionChange={setSelectedQuotes}
            editSelection={editSelection}
            loading={isFetchingSections}
            onBack={() => navigate(`/createproposalbuilder?token=${urlParamToken}`)}
            onNext={async () => {
              try {
                setIsFetchingSections(true);
                const res = await fetchProposalSectionStatus();

                if (res?.data?.success) {
                  setSectionStatus(res.data.data);
                  setCompletedSteps([1]);
                  setCurrentStep(2);
                }
              } catch (err) {
                console.error("GetProposalSectionStatus failed", err);
              } finally {
                setIsFetchingSections(false);
              }
            }}
          />
        )}
        {currentStep === 2 && (
          <ProposalSection
            proposalData={selectedProposalData}
            sectionStatus={sectionStatus}
            onBack={() => {
              setCompletedSteps([]);
              setCurrentStep(1);
            }}
          />
        )}
      </Container>
    </Box>
  );
};

export default ProposalBuilderCommonComponent;
